import { computed, inject, Injectable, signal } from '@angular/core';
import {
  collection,
  doc,
  Firestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
} from '@angular/fire/firestore';
import { Notification } from '../models';
import { AuthStore } from '../auth/auth.store';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly firestore = inject(Firestore);
  private readonly authStore = inject(AuthStore);

  private readonly _notifications = signal<Notification[]>([]);
  private readonly _loading = signal(false);
  private unsubscribe: (() => void) | null = null;

  readonly notifications = this._notifications.asReadonly();
  readonly loading = this._loading.asReadonly();

  readonly unreadCount = computed(
    () => this._notifications().filter((n) => !n.read).length
  );

  readonly unreadNotifications = computed(() =>
    this._notifications().filter((n) => !n.read)
  );

  private get tenantId(): string {
    const id = this.authStore.tenantId();
    if (!id) {
      throw new Error('No tenant selected');
    }
    return id;
  }

  private get userId(): string {
    const id = this.authStore.userId();
    if (!id) {
      throw new Error('No user authenticated');
    }
    return id;
  }

  subscribe(): void {
    if (this.unsubscribe) {
      return;
    }

    const notifCollection = collection(
      this.firestore,
      `tenants/${this.tenantId}/notifications`
    );

    const q = query(
      notifCollection,
      where('userId', '==', this.userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    this._loading.set(true);

    this.unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifications = snapshot.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            userId: data['userId'],
            type: data['type'],
            title: data['title'],
            body: data['body'],
            link: data['link'],
            read: data['read'],
            createdAt: data['createdAt']?.toDate(),
          } as Notification;
        });
        this._notifications.set(notifications);
        this._loading.set(false);
      },
      (error) => {
        console.error('Notification subscription error:', error);
        this._loading.set(false);
      }
    );
  }

  unsubscribeFromNotifications(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this._notifications.set([]);
  }

  async markAsRead(notificationId: string): Promise<void> {
    const notifRef = doc(
      this.firestore,
      `tenants/${this.tenantId}/notifications/${notificationId}`
    );
    await updateDoc(notifRef, { read: true });
  }

  async markAllAsRead(): Promise<void> {
    const unread = this.unreadNotifications();
    if (unread.length === 0) {
      return;
    }

    const batch = writeBatch(this.firestore);

    for (const notification of unread) {
      const notifRef = doc(
        this.firestore,
        `tenants/${this.tenantId}/notifications/${notification.id}`
      );
      batch.update(notifRef, { read: true });
    }

    await batch.commit();
  }
}
